import { el } from "../core/dom";
import { injectCheckboxStyles } from "../styles/inject";
import { verifyGuardianStatus } from "../api";
import {
  getConsentState,
  updateConsentState,
} from "../services/storage";
import { resolveEndpoints } from "../shared/endpoints";
import type { DigiLockerCallbackOptions } from "../types";
import type { GuardianVerificationErrorCode } from "../api/types";

function getErrorMessage(errorCode?: GuardianVerificationErrorCode | string | null): string {
  switch (errorCode) {
    case "GUARDIAN_UNDER_18":
      return "Guardian verification failed: The guardian must be 18 years or older.";
    case "SESSION_EXPIRED":
      return "Verification session has expired. Please restart the verification process.";
    case "TOKEN_FAILED":
      return "DigiLocker authentication failed. Please try again.";
    case "DIGILOCKER_AUTH_FAILED":
      return "DigiLocker authorization was cancelled or failed. Please try again.";
    case "NO_AUTH_CODE":
      return "Missing authorization code from DigiLocker. Please try again.";
    default:
      return "Verification failed. Please try again.";
  }
}

export function handleDigilockerCallback(options: DigiLockerCallbackOptions): void {
  // The DigiLocker callback page is a standalone screen, not embedded in a
  // host site, so styles can safely live in document.head here.
  injectCheckboxStyles(document);

  const params = new URLSearchParams(window.location.search);
  const sessionToken = params.get("session_token");
  const urlStatus = params.get("status") as "verified" | "failed" | "expired" | null;
  const verificationReference = params.get("verification_reference");
  const errorCode = params.get("error_code") as GuardianVerificationErrorCode | null;

  const persistedState = getConsentState();
  const originalUrl = persistedState?.originalUrl || "/";
  const storedBaseUrl = persistedState?.baseUrl;
  const storedSessionToken = persistedState?.sessionToken;
  const activeSessionToken = sessionToken || storedSessionToken;
  // The modal stores the consent server URL it used; an API host passed here
  // resolves to the same one.
  const apiBaseUrl = resolveEndpoints(options.baseUrl || storedBaseUrl).baseUrl;

  // Check if in iframe
  let isInIframe = false;
  try {
    isInIframe = window.self !== window.top;
  } catch {
    isInIframe = true;
  }

  // Render status UI
  const container = el("div", {
    style: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "200px",
      padding: "32px",
      fontFamily: "system-ui, -apple-system, sans-serif",
    },
  });

  const statusEl = el("p", {
    style: { fontSize: "16px", fontWeight: 600, color: "#101828", textAlign: "center" },
  });
  statusEl.textContent = "Processing verification...";

  const spinnerEl = el("div", {
    style: {
      width: "40px",
      height: "40px",
      border: "4px solid #f3f3f3",
      borderTop: "4px solid #3498db",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
      marginBottom: "16px",
    },
  });

  container.appendChild(spinnerEl);
  container.appendChild(statusEl);
  document.body.appendChild(container);

  const redirectToOriginal = (
    result: "verified" | "failed" | "expired",
    ref?: string
  ) => {
    updateConsentState({
      verificationResult: result,
      verificationReference: ref,
      flowState: result === "verified" ? "consent-form" : "error",
    });

    statusEl.textContent = result === "verified"
      ? "Verification successful! Redirecting..."
      : "Verification failed. Redirecting...";

    spinnerEl.style.display = "none";

    setTimeout(() => {
      window.location.href = originalUrl;
    }, 1500);
  };

  // Handle direct status from URL
  if (urlStatus === "verified" && verificationReference) {
    if (isInIframe && window.parent) {
      window.parent.postMessage({
        type: "DIGILOCKER_VERIFICATION_STATUS",
        status: "verified",
        verificationReference,
      }, window.location.origin);
      statusEl.textContent = "Verification successful!";
      spinnerEl.style.display = "none";
      return;
    }
    options.onSuccess?.(verificationReference);
    redirectToOriginal("verified", verificationReference);
    return;
  }

  if (urlStatus === "failed" || urlStatus === "expired") {
    const errorMsg = getErrorMessage(errorCode);
    if (isInIframe && window.parent) {
      window.parent.postMessage({
        type: "DIGILOCKER_VERIFICATION_STATUS",
        status: urlStatus,
        errorCode,
        error: errorMsg,
      }, window.location.origin);
      statusEl.textContent = errorMsg;
      statusEl.style.color = "#DC2626";
      spinnerEl.style.display = "none";
      return;
    }
    options.onError?.(new Error(errorMsg), errorCode || undefined);
    redirectToOriginal(urlStatus);
    return;
  }

  // No status in URL — poll the backend
  if (!activeSessionToken) {
    statusEl.textContent = "No verification session found.";
    statusEl.style.color = "#DC2626";
    spinnerEl.style.display = "none";
    options.onError?.(new Error("No session token available"));
    return;
  }

  let retryCount = 0;
  const MAX_RETRIES = 3;

  const pollStatus = async () => {
    try {
      const response = await verifyGuardianStatus({
        accessToken: options.accessToken,
        baseUrl: apiBaseUrl,
        sessionToken: activeSessionToken,
      });

      if (response.status === "verified") {
        const ref = response.verification_reference ||
          response.guardian_details?.verification_reference;

        if (isInIframe && window.parent) {
          window.parent.postMessage({
            type: "DIGILOCKER_VERIFICATION_STATUS",
            status: "verified",
            verificationReference: ref,
          }, window.location.origin);
          statusEl.textContent = "Verification successful!";
          spinnerEl.style.display = "none";
          return;
        }

        if (ref) options.onSuccess?.(ref);
        redirectToOriginal("verified", ref || undefined);
        return;
      }

      if (response.status === "failed" || response.status === "expired") {
        const errorMsg = getVerificationError(response.error_code, response.error);

        if (isInIframe && window.parent) {
          window.parent.postMessage({
            type: "DIGILOCKER_VERIFICATION_STATUS",
            status: response.status,
            errorCode: response.error_code,
            error: errorMsg,
          }, window.location.origin);
          statusEl.textContent = errorMsg;
          statusEl.style.color = "#DC2626";
          spinnerEl.style.display = "none";
          return;
        }

        options.onError?.(new Error(errorMsg), response.error_code || undefined);
        redirectToOriginal(response.status);
        return;
      }

      // Still in progress — retry with backoff
      retryCount++;
      if (retryCount > MAX_RETRIES) {
        const timeoutMsg = "Verification is taking too long. Please return to the original page.";
        statusEl.textContent = timeoutMsg;
        statusEl.style.color = "#DC2626";
        spinnerEl.style.display = "none";
        options.onError?.(new Error(timeoutMsg));
        redirectToOriginal("failed");
        return;
      }

      const delay = 3000 * Math.pow(2, retryCount - 1);
      statusEl.textContent = "Still processing... Please wait.";
      setTimeout(pollStatus, delay);
    } catch (error) {
      retryCount++;
      if (retryCount > MAX_RETRIES) {
        const errMsg = (error as Error).message || "Failed to check verification status.";
        statusEl.textContent = errMsg;
        statusEl.style.color = "#DC2626";
        spinnerEl.style.display = "none";
        options.onError?.(error as Error);
        redirectToOriginal("failed");
        return;
      }

      const delay = 3000 * Math.pow(2, retryCount - 1);
      setTimeout(pollStatus, delay);
    }
  };

  // Start first poll after 3s
  setTimeout(pollStatus, 3000);
}

function getVerificationError(errorCode: string | null, fallbackError: string | null): string {
  return getErrorMessage(errorCode) || fallbackError || "Verification failed. Please try again.";
}
